import React, { useState, useMemo, useEffect } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col,
  Badge,
  Input,
  Label,
  FormGroup,
  Progress,
} from "reactstrap";
import classnames from "classnames";
import { MapContainer, TileLayer, Marker, Popup, Polygon } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { tickets } from "../../helpers/backend_helper";
import { formatDate } from "../../helpers/formatDate";

// Custom fire icon
const fireIcon = new L.Icon({
  iconUrl: "fire.png", // Make sure this exists in your public assets
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const openFireIcon = new L.Icon({
  iconUrl: "BlueFire.png", // Make sure this exists in your public assets
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Fallback to default marker if fire icon is not available
const defaultIcon = new L.Icon.Default();

const ForestFireModal = ({
  isOpen,
  toggle,
  compData,
  createTicketFromLocation
}) => {
  const [activeTab, setActiveTab] = useState("1");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [createdTickets, setCreatedTickets] = useState({}); // Track created tickets by ID
  const [beats, setBeats] = useState([]);
  const [compartment, setCompartment] = useState([]);
  const [showGuardLocations, setShowGuardLocations] = useState(false);
  const [showOpenFire, setShowOpenFire] = useState(false);
const [  mergeTicket, setMergeTickets] = useState(null); 
  const [showCarLocations, setShowCarLocations] = useState(false);
  // New state for KML files
  const [kmlFiles, setKmlFiles] = useState([]);
  const [uploadedKmlData, setUploadedKmlData] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [currentUploadingFile, setCurrentUploadingFile] = useState(null);
  const [kmlLayerVisibility, setKmlLayerVisibility] = useState({}); // Track visibility for each KML file
  const [openFire, setOpenFire] = useState([]);

  // Maximum distance in kilometers to consider matching fire locations
  const MATCHING_DISTANCE_THRESHOLD = 0.5; // 500 meters

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };
  const fetchBeatsData = async () => {
    try {
      const response = await fetch(`Beats.json`);
      if (!response.ok) throw new Error(`Failed to load Beats.json`);
      const jsonData = await response.json();
      setBeats(jsonData);
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const fetchCompsData = async () => {
    try {
      const response = await fetch(`Comps.json`);
      if (!response.ok) throw new Error(`Failed to load Comps.json`);
      const jsonData = await response.json();
      setCompartment(jsonData);
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };
  
  const fetchOpenfireData = async () => {
    const payload = {
      act: "ticket",
      subact: "view",
      filter: {
        status: "Open",
        search: "",
      },
    };

    try {
      const response = await tickets(payload);
      if (response.data?.ticket) {
        const fires = response.data.ticket.filter(
          (fire) => fire.incidentTypeId === "1"
        );
        setOpenFire(fires);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

   // Function to find matching open fire by lat/long proximity
   const findMatchingOpenFire = (lat, lng) => {
    if (!openFire || openFire.length === 0) return null;
    
    // Find the closest open fire ticket
    let closestFire = null;
    let closestDistance = Infinity;
    
    openFire.forEach(fire => {
      if (fire.incidentLocation && fire.incidentLocation.lat && fire.incidentLocation.long) {
        const distance = calculateDistance(
          lat, 
          lng, 
          fire.incidentLocation.lat, 
          fire.incidentLocation.long
        );
        
        // If this fire is closer than the previous closest and within threshold
        if (distance < closestDistance && distance <= MATCHING_DISTANCE_THRESHOLD) {
          closestDistance = distance;
          closestFire = {
            ...fire,
            distance: distance
          };
        }
      }
    });
    
    return closestFire;
  };

  // Calculate the central point of a compartment from its coordinates
  const getCompCentralPoint = (comp) => {
    if (!comp.coordinates || comp.coordinates.length === 0) {
      return null;
    }

    // Calculate the average of all coordinate points
    let sumLat = 0;
    let sumLng = 0;

    comp.coordinates.forEach((point) => {
      sumLat += parseFloat(point[0]); // Assuming format is [lng, lat] based on your data
      sumLng += parseFloat(point[1]);
    });

    return {
      latitude: sumLng / comp.coordinates.length,
      longitude: sumLat / comp.coordinates.length,
    };
  };

  useEffect(() => {
    fetchBeatsData();
    fetchCompsData();
    fetchOpenfireData();
  }, []);

  // Find the center point for the map based on fire locations
  const getMapCenter = () => {
    if (uploadedKmlData.length === 0) return [19.9615, 79.2961]; // Default to Mumbai

    // Calculate the average of all coordinates
    const sumLat = uploadedKmlData.reduce((sum, loc) => sum + loc.lat, 0);
    const sumLng = uploadedKmlData.reduce((sum, loc) => sum + loc.lng, 0);

    return [sumLat / uploadedKmlData.length, sumLng / uploadedKmlData.length];
  };

  const handleMarkerClick = (location) => {
    setSelectedLocation(location);
  };
  
  function isPointInPolygon(point, polygon) {
    if (!polygon || !Array.isArray(polygon) || polygon.length < 3) {
      return false;
    }
    const x = point[0];
    const y = point[1];
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0];
      const yi = polygon[i][1];
      const xj = polygon[j][0];
      const yj = polygon[j][1];

      const intersect =
        yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }

    return inside;
  }
  
  const matchedData = uploadedKmlData
    .filter(kmlFile => kmlLayerVisibility[kmlFile.file.name] === true)
    .flatMap(kmlFile => 
      kmlFile.locations?.map(({ lng, lat, name, id }) => {
        const matchedComp = compartment
          .filter(comp => comp.divisionId)
          .find(comp => {
            if (!comp.coordinates || comp.coordinates.length < 3) return false;
            const polygonCoords = comp.coordinates.map(([long, lati]) => [
              parseFloat(long),
              parseFloat(lati),
            ]);
      
            return isPointInPolygon([lng, lat], polygonCoords);
          });
        
        const distance =
          matchedComp && getCompCentralPoint(matchedComp)
            ? calculateDistance(lat, lng, matchedComp.longitude, matchedComp.latitude)
            : null;
    
        // Check if this fire location matches with any open fire tickets
        const matchingOpenFire = findMatchingOpenFire(lat, lng);
    
        return {
          lng,
          lat,
          name,
          id,
          matched: !!matchedComp,
          compData: matchedComp,
          distance,
          matchingOpenFire, // Add the matching open fire ticket if exists
        };
      }) || []
    );

 

  // Function to handle merging tickets
  const handleMergeTicket = async (location, openFireTicket) => {
    // Mark this location as having a ticket created or merged
    setCreatedTickets((prev) => ({
      ...prev,
      [location.id]: true,
    }));
    
    // Call the mergeTickets function with the necessary data
    if (openFireTicket) {
      const data = {
        count: openFireTicket.count++,
        description: openFireTicket.description +=  ` ${location.lat}, ${location.lng}`,
        ...openFireTicket,
      };
      const payload = {
        act: "ticket",
        subact: "update",
        info: data,
      };
    
      try {
        const response = await tickets(payload);
      }catch (error) {
        console.error("Error merging tickets:", error);
        alert("Error merging tickets. Please try again.");
      }
      setMergeTickets(location, openFireTicket);
    } else {
      console.warn("Merge tickets function not provided");
      // createTicketFromLocation(location);
    }
  };

  // Group fire locations by compId for the table view
  const groupedByCompId = useMemo(() => {
    // First, separate matched and unmatched locations
    const matched = matchedData.filter((loc) => loc.matched);
    const unmatched = matchedData.filter((loc) => !loc.matched);

    // Group matched locations by compId
    const groupedMatched = matched.reduce((acc, location) => {
      const compId = location.compData.compId;

      if (!acc[compId]) {
        acc[compId] = {
          representative: location,
          locations: [location],
          count: 1,
        };
      } else {
        acc[compId].locations.push(location);
        acc[compId].count += 1;
      }

      return acc;
    }, {});

    // Convert grouped object to array
    const groupedMatchedArray = Object.values(groupedMatched);

    // Create final table data with one entry per compId for matched locations
    // followed by all unmatched locations
    return [
      ...groupedMatchedArray,
      ...unmatched.map((loc) => ({
        representative: loc,
        locations: [loc],
        count: 1,
      })),
    ];
  }, [matchedData]);

  // Haversine formula to calculate distance between two points
  function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) *
        Math.cos(deg2rad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c; // Distance in km
    return d;
  }

  function deg2rad(deg) {
    return deg * (Math.PI / 180);
  }

  // Handle ticket creation for single location
  const handleCreateTicket = (location) => {
    // Mark this location as having a ticket created
    setCreatedTickets((prev) => ({
      ...prev,
      [location.id]: true,
    }));

    // Call the original function to create the ticket
    createTicketFromLocation(location);
  };

  // Handle ticket creation for grouped locations
  const createTicketForGroup = (group) => {
    // If there's only one location in the group, use the handler for a single location
    if (group.count === 1) {
      // Check if this location has a matching open fire
      if (group.representative.matchingOpenFire) {
        handleMergeTicket(group.representative, group.representative.matchingOpenFire);
      } else {
        handleCreateTicket(group.representative);
      }
      return;
    }

    // Mark all locations in this group as having tickets created
    const updatedTickets = { ...createdTickets };
    group.locations.forEach((location) => {
      updatedTickets[location.id] = true;
    });
    setCreatedTickets(updatedTickets);

    const locationDescriptions = group.locations
      .map((loc, index) => `${loc.lng},${loc.lat}`)
      .join(";");
    ;

    const compData = group.representative.compData;

    // Create an aggregated location object for the ticket
    const aggregatedLocation = {
      compData,
      multiLocations: locationDescriptions,
      lat: group.representative.lat,
      lng: group.representative.lng,
      count: group.count,
      // Include matching open fire if the representative location has one
      matchingOpenFire: group.representative.matchingOpenFire,
    };
    
    // Check if this group has a matching open fire
    if (aggregatedLocation.matchingOpenFire) {
      handleMergeTicket(aggregatedLocation, aggregatedLocation.matchingOpenFire);
    } else {
      createTicketFromLocation(aggregatedLocation);
    }
  };

  // Check if a location or group has a ticket created
  const isTicketCreated = (location) => {
    return createdTickets[location.id] === true;
  };

  // Check if a group has tickets created for all locations
  const isGroupTicketCreated = (group) => {
    if (group.count === 1) {
      return isTicketCreated(group.representative);
    }
    return group.locations.every((location) => createdTickets[location.id]);
  };

  // Simplified checkbox handlers
  const handleGuardLocationsChange = (e) => {
    setShowGuardLocations(e);
  };
  
  // Simplified checkbox handlers
  const handleOpenFireChange = (e) => {
    setShowOpenFire(e);
  };
  
  const handleCarLocationsChange = (e) => {
    setShowCarLocations(e);
  };
  
  // KML file parsing and handling
  const parseKMLFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const kmlContent = e.target.result;
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(kmlContent, "text/xml");
        const placemarks = xmlDoc.getElementsByTagName("Placemark");

        const parsedLocations = Array.from(placemarks)
          .map((placemark) => {
            const coordinatesElement =
              placemark.getElementsByTagName("coordinates")[0];
            const nameElement = placemark.getElementsByTagName("name")[0];

            if (coordinatesElement) {
              const coordinates = coordinatesElement.textContent
                .trim()
                .split(",");
              const name = nameElement
                ? nameElement.textContent.trim()
                : "Unnamed Location";

              if (coordinates && coordinates.length >= 2) {
                return {
                  lng: parseFloat(coordinates[0]),
                  lat: parseFloat(coordinates[1]),
                  name: name,
                  id: `kml-${Math.random().toString(36).substr(2, 9)}`,
                };
              }
            }
            return null;
          })
          .filter((loc) => loc !== null);

        resolve({
          file: file,
          locations: parsedLocations,
        });
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsText(file);
    });
  };

  // Remove a specific KML file
  const removeKMLFile = (fileToRemove) => {
    setKmlFiles((prevFiles) =>
      prevFiles.filter((file) => file !== fileToRemove)
    );
    setUploadedKmlData((prevData) =>
      prevData.filter((data) => data.file !== fileToRemove)
    );
    setKmlLayerVisibility((prev) => {
      const newState = { ...prev };
      delete newState[fileToRemove.name];
      return newState;
    });
  };

  const handleSequentialKMLUpload = async (event) => {
    const files = Array.from(event.target.files);

    if (files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const parsedFiles = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setCurrentUploadingFile(file);

        // Update progress
        setUploadProgress(Math.floor((i / files.length) * 100));

        // Parse current file
        const parsedFile = await parseKMLFile(file);
        parsedFiles.push(parsedFile);

        // Initialize visibility state for this file
        setKmlLayerVisibility((prev) => ({
          ...prev,
          [file.name]: true,
        }));
      }

      // Update states after all files are processed
      setKmlFiles((prevFiles) => [...prevFiles, ...files]);
      setUploadedKmlData((prevData) => [...prevData, ...parsedFiles]);

      // Reset upload states
      setIsUploading(false);
      setCurrentUploadingFile(null);
      setUploadProgress(100);
    } catch (error) {
      console.error("Error parsing KML files:", error);
      alert("Error parsing KML files. Please check the file format.");
      setIsUploading(false);
      setCurrentUploadingFile(null);
    }
  };

  const renderUploadProgress = () => {
    if (!isUploading) return null;

    return (
      <div className="mt-2">
        <Progress value={uploadProgress} color="primary">
          {currentUploadingFile
            ? `Uploading ${currentUploadingFile.name}... ${uploadProgress}%`
            : `Processing... ${uploadProgress}%`}
        </Progress>
      </div>
    );
  };
  
  const toggleKMLLayerVisibility = (fileName) => {
    setKmlLayerVisibility((prev) => ({
      ...prev,
      [fileName]: !prev[fileName],
    }));
  };
  
  const renderKMLFilesList = () => {
    if (kmlFiles.length === 0) return null;

    return (
      <div className="mt-2">
        <strong>Additional Fire Locations:</strong>
        {kmlFiles.map((file, index) => (
          <div
            key={index}
            className="d-flex justify-content-between align-items-center mb-2"
          >
            <div className="d-flex align-items-center">
              <Input
                type="checkbox"
                checked={kmlLayerVisibility[file.name]==true || false}
                onClick={(e) => toggleKMLLayerVisibility(file.name)}
                className="mr-2"
              />
              <span className="ml-2">{file.name}</span>
            </div>
            <Button
              color="danger"
              size="sm"
              onClick={() => removeKMLFile(file)}
              disabled={isUploading}
            >
              Remove
            </Button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle}>Forest Fire Data </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="kmlUpload">Upload KML Files</Label>
          <div className="d-flex align-items-center">
            <Input
              type="file"
              id="kmlUpload"
              name="kmlUpload"
              multiple
              accept=".kml"
              onChange={handleSequentialKMLUpload}
              disabled={isUploading}
            />
        
          </div>
          <small className="text-muted">
            Upload FIRMS/NASA KML file with forest fire data
          </small>
          {renderUploadProgress()}
          {renderKMLFilesList()}
        </FormGroup>

        <div className="d-flex gap-3">
          <div className="form-check">
            <Input
              type="checkbox"
              className="form-check-input"
              id="showGuards"
              checked={showGuardLocations == true}
              onClick={(e) => {
                e.stopPropagation();
                handleGuardLocationsChange(!showGuardLocations);
              }}
            />
            <label className="form-check-label" htmlFor="showGuards">
              Show All Beats
            </label>
          </div>
          <div className="form-check">
            <Input
              type="checkbox"
              className="form-check-input"
              id="showCars"
              checked={showCarLocations}
              onClick={(e) => {
                e.stopPropagation();
                handleCarLocationsChange(!showCarLocations);
              }}
            />
            <label className="form-check-label" htmlFor="showCars">
              Show All Compartments
            </label>
          </div>
          <div className="form-check">
            <Input
              type="checkbox"
              className="form-check-input"
              id="showGuards"
              checked={showOpenFire == true}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenFireChange(!showOpenFire);
              }}
            />
            <label className="form-check-label" htmlFor="showGuards">
              Show Open Fire
            </label>
          </div>
        </div>

        <Nav tabs>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "1" })}
              onClick={() => toggleTab("1")}
            >
              नकाशा (Map)
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "2" })}
              onClick={() => toggleTab("2")}
            >
              तालिका (Table)
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab} className="pt-3">
          <TabPane tabId="1">
            <div style={{ height: "70vh", width: "100%" }}>
              {
                <MapContainer
                  center={getMapCenter()}
                  zoom={9}
                  style={{ height: "100%", width: "100%" }}
                >
                  <TileLayer
                    url="http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}"
                    subdomains={["mt0", "mt1", "mt2", "mt3"]}
                  />

                  {showGuardLocations &&
                    beats?.map((beat, index) => {
                      if (!beat.coordinates || beat.coordinates.length < 3)
                        return null;
                      const coordinates = beat.coordinates
                        .filter(
                          (coord) => Array.isArray(coord) && coord.length === 2
                        )
                        .map((coord) => [
                          parseFloat(coord[1]),
                          parseFloat(coord[0]),
                        ])
                        .filter(
                          (coord) => !isNaN(coord[1]) && !isNaN(coord[0])
                        );

                      if (coordinates.length > 2) {
                        return (
                          <Polygon
                            key={`${beat}-${index}`}
                            positions={coordinates}
                            pathOptions={{
                              color: "#FFA500",
                              fillColor: "#FFA500",
                              fillOpacity: 0.1,
                              weight: 1,
                            }}
                          >
                            <Popup>
                              <>
                                <label>Beat Name:</label> {beat.name} {beat.beatName}
                                <br />
                                <label>Round Name: </label> {beat.roundName}
                                <br />
                                <label>Range Neme:</label> {beat.rangeName}
                              </>
                            </Popup>
                          </Polygon>
                        );
                      }
                      return null;
                    })}

                  {showCarLocations &&
                    compartment?.map((beat, index) => {
                      if (!beat.coordinates || beat.coordinates.length < 3)
                        return null;
                      const coordinates = beat.coordinates
                        .filter(
                          (coord) => Array.isArray(coord) && coord.length === 2
                        )
                        .map((coord) => [
                          parseFloat(coord[1]),
                          parseFloat(coord[0]),
                        ])
                        .filter(
                          (coord) => !isNaN(coord[1]) && !isNaN(coord[0])
                        );

                      if (coordinates.length > 2) {
                        return (
                          <Polygon
                            key={`${beat}-${index}`}
                            positions={coordinates}
                            pathOptions={{
                              color: "#FF9000",
                              fillColor: "#ff0000",
                              fillOpacity: 0,
                              weight: 1,
                              dashArray: "5, 5", // Creates a dotted/dashed border
                            }}
                          >
                            <Popup>
                              <>
                                <label>CompId:</label> {beat.compId}
                                <br />
                                <label>Beat Name:</label> {beat.beatName}
                                <br />
                                <label>Beat Id:</label> {beat.beatId}
                                <br />
                                <label>Round Name: </label> {beat.roundName}
                                <br />
                                <label>Range Neme:</label> {beat.rangeName}
                              </>
                            </Popup>
                          </Polygon>
                        );
                      }
                      return null;
                    })}

                  {matchedData?.map((location) => (
                    <Marker
                      key={location.id}
                      position={[location.lat, location.lng]}
                      icon={fireIcon || defaultIcon}
                      eventHandlers={{
                        click: () => handleMarkerClick(location),
                      }}
                    >
                      <Popup>
                        <div>
                          <h6>{location.name}</h6>
                          <p>
                            <strong>Coordinates:</strong>{" "}
                            {location.lat.toFixed(5)}, {location.lng.toFixed(5)}
                          </p>
                          {location.matched && (
                            <div>
                              <p>
                                <strong>Matched:</strong>{" "}
                                {location.compData.compId} -{" "}
                                {location.compData.beatName}
                                <br />
                                <strong>Old CompId:</strong>{" "}
                                {location?.compData?.oldCompN}
                                <br />
                                <strong>Division:</strong>{" "}
                                {location.compData.divisionName}
                                <br />
                                <strong>Range:</strong>{" "}
                                {location.compData.rangeName}
                                <br />
                                <strong>Round:</strong>{" "}
                                {location.compData.roundName}
                                <br />
                                <strong>Beats:</strong>{" "}
                                {location.compData.beatName}
                                <br />
                                <strong>CompId:</strong>{" "}
                                {location.compData.compId}
                                <br />
                              </p>
                            </div>
                          )}
                          
                          {/* Display matching open fire information if available */}
                          {location.matchingOpenFire && (
                            <div className="mt-2 p-2 border border-warning bg-light">
                              <p className="mb-1">
                                <strong>Open Fire Ticket:</strong>
                              </p>
                              <p className="mb-1">
                                <strong>Ticket No:</strong> {location.matchingOpenFire.serialId}
                                <br />
                               
                                <strong>Incident Date:</strong> {formatDate(location.matchingOpenFire.incidentDate)}
                              </p>
                              <Button
                                color="warning"
                                size="sm"
                                className="mt-1"
                                onClick={() => handleMergeTicket(location, location.matchingOpenFire)}
                                disabled={isTicketCreated(location)}
                              >
                                {isTicketCreated(location)
                                  ? "Ticket Merged)"
                                  : "Merge Ticket"}
                              </Button>
                            </div>
                          )}
                          
                          {!location.matchingOpenFire && (
                            <Button
                              color={
                                isTicketCreated(location) ? "success" : "primary"
                              }
                              size="sm"
                              className="mt-2"
                              onClick={() => handleCreateTicket(location)}
                              disabled={isTicketCreated(location)}
                            >
                              {isTicketCreated(location)
                                ? "Ticket Created"
                                : "Create Ticket"}
                            </Button>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  ))}

                  {openFire && showOpenFire && openFire?.map((location) => (
                    <Marker
                      key={location.id}
                      position={[location?.incidentLocation?.lat, location?.incidentLocation?.long]}
                      icon={openFireIcon || defaultIcon}
                      eventHandlers={{
                        click: () => handleMarkerClick(location),
                      }}
                    >
                      <Popup>
                        <div>
                          <h6>{location.name}</h6>
                          <p>
                            <strong>Incident Type:</strong>{" "}
                            {location.incidentTypeName}
                            <br />
                            <strong>Ticket No:</strong> {location.serialId}
                            <br />
                            <strong>Incident Date:</strong>{" "}
                            {formatDate(location.incidentDate)}
                            <br />  
                          
                          
                            <strong>Incident Status:</strong>{" "}
                            {location.status}
                            <br />
                            <strong>Coordinates:</strong>{" "}
                            {location?.incidentLocation?.lat?.toFixed(5)}, {location?.incidentLocation?.long?.toFixed(5)}
                          </p>
                          {/* <Button
                            color={
                              isTicketCreated(location) ? "success" : "primary"
                            } 
                            size="sm"
                            className="mt-2"
                            onClick={() => handleCreateTicket(location)}
                            disabled={isTicketCreated(location)}
                          >
                            {isTicketCreated(location)
                              ? "तिकीट तयार केली (Ticket Created)"
                              : "तिकीट तयार करा (Create Ticket)"}
                          </Button> */}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              }
            </div>
          </TabPane>

          <TabPane tabId="2">
            <div className="table-responsive">
              <Table className="table-bordered">
                <thead>
                  <tr>
                    <th>Sr.</th>
                    <th>Location</th>
                    <th>Division</th>
                    <th>Range</th>
                    <th>Round</th>
                    <th>Beat</th>
                    <th>Count</th>
                    <th>अक्षांश/रेखांश (Lat/Long)</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedByCompId?.map((group, index) => {
                      const location = group.representative;
                      const isMatched = location.matched;
                      const ticketCreated = isGroupTicketCreated(group);

                      return (
                        <tr
                          key={index}
                          className={isMatched ? "table-warning" : ""}
                        >
                          <td>{index + 1}</td>
                          <td>
                            {isMatched
                              ? `Comp ID: ${location.compData.compId}`
                              : location.name}
                          </td>

                          <td>
                            {isMatched ? location.compData.divisionName : "-"}
                          </td>
                          <td>
                            {isMatched ? location.compData.rangeName : "-"}
                          </td>
                          <td>
                            {isMatched ? location.compData.roundName : "-"}
                          </td>

                          <td>
                            {isMatched ? location.compData.beatName : "-"}
                          </td>
                          <td className="text-center">
                            {group.count > 1 ? (
                              <Badge color="info" pill>
                                {group.count}
                              </Badge>
                            ) : (
                              "1"
                            )}
                          </td>
                          <td>
                            {location.lat.toFixed(5)}, {location.lng.toFixed(5)}
                          </td>
                          <td>
  {location.matchingOpenFire ? (
    <Button
      color={ticketCreated ? "success" : "warning"}
      size="sm"
      onClick={() => handleMergeTicket(location, location.matchingOpenFire)}
      disabled={ticketCreated}
    >
      {ticketCreated
        ? "Ticket Merged"
        : "Merge Ticket"}
    </Button>
  ) : (
    <Button
      color={ticketCreated ? "success" : "primary"}
      size="sm"
      onClick={() => createTicketForGroup(group)}
      disabled={ticketCreated}
    >
      {ticketCreated
        ? "Ticket Created)"
        : "Create Ticket"}
    </Button>
  )}
</td>
                        </tr>
                      );
                    })}
                </tbody>
              </Table>
            </div>
          </TabPane>
        </TabContent>
      </ModalBody>
    </Modal>
  );
};

export default ForestFireModal;
